/**
 * Builds a human-readable message explaining why the best rate is cheaper,
 * e.g. "Weekly rate applied — even for a 3-day rental."
 */
export function buildSavingsMessage(result: RateCalculationResult): string | null {
  if (result.bestRate >= result.standardRate) return null;

  const { months, weeks, days } = result.bestBreakdown;
  const parts: string[] = [];

  if (months > 0) parts.push(months === 1 ? '4-week' : `${months}x 4-week`);
  if (weeks > 0) parts.push(weeks === 1 ? 'Weekly' : `${weeks}x Weekly`);

  if (parts.length === 0) return null;

  const rateName = parts.join(' + ') + ' rate applied';
  return `${rateName} — even for a ${result.rentalDays}-day rental.`;
}

export interface RateCalculationInput {
  pricePerDay: number;
  pricePerWeek: number;
  pricePerMonth: number;
  rentalDays: number;
}

export interface RateBreakdown {
  months: number;
  weeks: number;
  days: number;
}

export interface RateCalculationResult {
  bestRate: number;
  standardRate: number;
  rentalDays: number;
  bestBreakdown: RateBreakdown;
}

const DAYS_PER_WEEK = 7;
const DAYS_PER_4_WEEKS = 28;

/**
 * Calculates the greedy "standard" rate: fit as many 4-week blocks as possible,
 * then weeks, then charge remaining days at the daily rate. No rounding up.
 */
function calculateStandardRate(
  pricePerDay: number,
  pricePerWeek: number,
  pricePerMonth: number,
  rentalDays: number,
): number {
  const months = Math.floor(rentalDays / DAYS_PER_4_WEEKS);
  const daysAfterMonths = rentalDays - months * DAYS_PER_4_WEEKS;

  const weeks = Math.floor(daysAfterMonths / DAYS_PER_WEEK);
  const remainingDays = daysAfterMonths - weeks * DAYS_PER_WEEK;

  return months * pricePerMonth + weeks * pricePerWeek + remainingDays * pricePerDay;
}

/**
 * Calculates the most cost-effective rental rate by comparing all sensible
 * combinations of 4-week, weekly, and daily rates — including "rounding up"
 * to the next week or 4-week block when that's cheaper than paying for
 * leftover days individually.
 *
 * Returns:
 * - bestRate: the cheapest possible total
 * - standardRate: the greedy breakdown (months → weeks → remaining days)
 *   Used as the strikethrough price when bestRate < standardRate.
 */
export function calculateBestRate({
  pricePerDay,
  pricePerWeek,
  pricePerMonth,
  rentalDays,
}: RateCalculationInput): RateCalculationResult {
  const defaultBreakdown: RateBreakdown = { months: 0, weeks: 0, days: 0 };

  if (rentalDays <= 0) {
    return { bestRate: 0, standardRate: 0, rentalDays: 0, bestBreakdown: defaultBreakdown };
  }

  const standardRate = calculateStandardRate(pricePerDay, pricePerWeek, pricePerMonth, rentalDays);

  let bestRate = standardRate;
  let bestBreakdown: RateBreakdown = {
    months: Math.floor(rentalDays / DAYS_PER_4_WEEKS),
    weeks: Math.floor((rentalDays % DAYS_PER_4_WEEKS) / DAYS_PER_WEEK),
    days: (rentalDays % DAYS_PER_4_WEEKS) % DAYS_PER_WEEK,
  };

  const maxMonths = Math.floor(rentalDays / DAYS_PER_4_WEEKS);

  for (let months = 0; months <= maxMonths + 1; months++) {
    const daysAfterMonths = rentalDays - months * DAYS_PER_4_WEEKS;

    if (daysAfterMonths < 0) {
      const costRoundUpMonth = months * pricePerMonth;
      if (costRoundUpMonth < bestRate) {
        bestRate = costRoundUpMonth;
        bestBreakdown = { months, weeks: 0, days: 0 };
      }
      break;
    }

    const maxWeeks = Math.floor(daysAfterMonths / DAYS_PER_WEEK);

    for (let weeks = maxWeeks; weeks <= maxWeeks + 1; weeks++) {
      const remainingDays = daysAfterMonths - weeks * DAYS_PER_WEEK;

      const cost =
        months * pricePerMonth +
        weeks * pricePerWeek +
        (remainingDays > 0 ? remainingDays * pricePerDay : 0);

      if (cost < bestRate) {
        bestRate = cost;
        bestBreakdown = { months, weeks, days: Math.max(remainingDays, 0) };
      }
    }
  }

  return {
    bestRate: Math.round(bestRate * 100) / 100,
    standardRate: Math.round(standardRate * 100) / 100,
    rentalDays,
    bestBreakdown,
  };
}
